<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Input from '../../../../../components-next/input/Input.vue';
import Button from '../../../../../components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();

const props = defineProps({
  items: {
    type: Array,
    default: () => []
  },
  required: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update', 'validation']);

// Verificar se estamos no modo de edição
const isEditMode = computed(() => route.query.mode === 'edit');

// Estado de busca de CEP
const searchingCEPIndex = ref(-1);

// Estados para controlar campos tocados
const touchedItems = ref({});

// Lista de estados brasileiros
const brazilianStates = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' }
];

// Regex para validações
const streetRegex = /^[\p{L}\s\d.,\-]+$/u;
const numberRegex = /^[0-9]+$/;
const neighborhoodRegex = /^[\p{L}\s\d.,\-]+$/u;
const cityRegex = /^[\p{L}\s\-']+$/u;

// Funções de validação
const validateStreet = (value) => {
  if (!value || !value.trim()) {
    return 'Rua é obrigatório';
  }
  if (value.length < 3) {
    return 'Rua deve ter pelo menos 3 caracteres';
  }
  if (!streetRegex.test(value)) {
    return 'Use apenas letras, números, espaços e pontuação básica';
  }
  return '';
};

const validateNumber = (value) => {
  if (!value || !value.trim()) {
    return 'Número é obrigatório';
  }
  if (!numberRegex.test(value)) {
    return 'Use apenas números';
  }
  return '';
};

const validateZipCode = (value) => {
  const clean = value.replace(/\D/g, '');
  if (!clean) {
    return 'CEP é obrigatório';
  }
  if (clean.length !== 8) {
    return 'CEP deve ter 8 dígitos';
  }
  return '';
};

const validateNeighborhood = (value) => {
  if (!value || !value.trim()) {
    return 'Bairro é obrigatório';
  }
  if (value.length < 3) {
    return 'Bairro deve ter pelo menos 3 caracteres';
  }
  if (!neighborhoodRegex.test(value)) {
    return 'Use apenas letras, números, espaços e pontuação básica';
  }
  return '';
};

const validateCity = (value) => {
  if (!value || !value.trim()) {
    return 'Cidade é obrigatório';
  }
  if (value.length < 3) {
    return 'Cidade deve ter pelo menos 3 caracteres';
  }
  if (!cityRegex.test(value)) {
    return 'Use apenas letras, espaços, hífens e apóstrofos';
  }
  return '';
};

const validateState = (value) => {
  if (!value || !value.trim()) {
    return 'Estado é obrigatório';
  }
  const upperValue = value.toUpperCase();
  const isValid = brazilianStates.some(state => state.sigla === upperValue);
  if (!isValid) {
    return 'Selecione um estado válido';
  }
  return '';
};

// Função para formatar CEP
const formatZipCode = (value) => {
  const nums = value.replace(/\D/g, '');
  return nums.length <= 5 ? nums : `${nums.slice(0,5)}-${nums.slice(5,8)}`;
};

// Função para validar um item completo
const validateLocation = (location, index) => {
  const errors = {
    streetError: '',
    numberError: '',
    zipCodeError: '',
    neighborhoodError: '',
    cityError: '',
    stateError: ''
  };

  // Só valida campos que foram tocados ou no modo de edição
  if (touchedItems.value[`${index}-street`] || isEditMode.value) {
    errors.streetError = validateStreet(location.street);
  }
  if (touchedItems.value[`${index}-number`] || isEditMode.value) {
    errors.numberError = validateNumber(location.number);
  }
  if (touchedItems.value[`${index}-zipCode`] || isEditMode.value) {
    errors.zipCodeError = validateZipCode(location.zipCode);
  }
  if (touchedItems.value[`${index}-neighborhood`] || isEditMode.value) {
    errors.neighborhoodError = validateNeighborhood(location.neighborhood);
  }
  if (touchedItems.value[`${index}-city`] || isEditMode.value) {
    errors.cityError = validateCity(location.city);
  }
  if (touchedItems.value[`${index}-state`] || isEditMode.value) {
    errors.stateError = validateState(location.state);
  }

  return errors;
};

// Computed para verificar se todos os itens são válidos
const areAllLocationsValid = computed(() => {
  
  if (!props.items || props.items.length === 0) {
    return true;
  }
  
  const validation = props.items.every((location, index) => {
    
    // Verifica se há erros
    const hasErrors = location.streetError || location.numberError || 
                     location.zipCodeError || location.neighborhoodError || 
                     location.cityError || location.stateError;
    
    if (hasErrors) {
      return false;
    }
    
    // Verifica se os campos obrigatórios estão preenchidos
    const hasRequiredFields = location.street && location.number && 
                             location.zipCode && location.neighborhood && 
                             location.city && location.state;
    
    return hasRequiredFields;
  });
  
  return validation;
});

// Debounce timer for field updates
let updateTimer = null;

// Atualizar um campo específico
const updateLocationField = (index, field, value) => {
  const updatedItems = [...props.items];
  updatedItems[index][field] = value;
  
  // Validar o campo
  const errors = validateLocation(updatedItems[index], index);
  Object.assign(updatedItems[index], errors);
  
  // Se o campo foi tocado, validar campos relacionados também
  if (touchedItems.value[`${index}-${field}`]) {
    // Marcar todos os campos como tocados se um foi modificado
    ['street', 'number', 'zipCode', 'neighborhood', 'city', 'state'].forEach(f => {
      if (touchedItems.value[`${index}-${f}`]) {
        const fieldErrors = validateLocation(updatedItems[index], index);
        Object.assign(updatedItems[index], fieldErrors);
      }
    });
  }
  
  // Debounce emit to prevent excessive updates
  clearTimeout(updateTimer);
  updateTimer = setTimeout(() => {
    emit('update', updatedItems);
  }, 100);
};

// Marcar campo como tocado
const markFieldAsTouched = (index, field) => {
  touchedItems.value[`${index}-${field}`] = true;
  
  // Revalidar o item
  const updatedItems = [...props.items];
  const errors = validateLocation(updatedItems[index], index);
  Object.assign(updatedItems[index], errors);
  
  emit('update', updatedItems);
};

// Remover uma localização
const removeLocation = (index) => {
  const updatedItems = props.items.filter((_, i) => i !== index);
  
  // Limpar touched items do item removido
  Object.keys(touchedItems.value).forEach(key => {
    if (key.startsWith(`${index}-`)) {
      delete touchedItems.value[key];
    }
  });
  
  // Reajustar índices dos touched items
  const newTouchedItems = {};
  Object.keys(touchedItems.value).forEach(key => {
    const [oldIndex, field] = key.split('-');
    const numIndex = parseInt(oldIndex);
    if (numIndex > index) {
      newTouchedItems[`${numIndex - 1}-${field}`] = touchedItems.value[key];
    } else {
      newTouchedItems[key] = touchedItems.value[key];
    }
  });
  touchedItems.value = newTouchedItems;
  
  emit('update', updatedItems);
};

// Buscar CEP
const searchCEP = async (index) => {
  const location = props.items[index];
  const cleanCEP = location.zipCode.replace(/\D/g, '');
  
  if (cleanCEP.length !== 8) {
    return;
  }
  
  searchingCEPIndex.value = index;
  
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
    const data = await response.json();
    
    if (!data.erro) {
      const updatedItems = [...props.items];
      updatedItems[index] = {
        ...updatedItems[index],
        street: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || ''
      };
      
      // Marcar campos preenchidos automaticamente como tocados
      ['street', 'neighborhood', 'city', 'state'].forEach(field => {
        touchedItems.value[`${index}-${field}`] = true;
      });
      
      // Validar todos os campos
      const errors = validateLocation(updatedItems[index], index);
      Object.assign(updatedItems[index], errors);
      
      emit('update', updatedItems);
      
      // Focar no campo número
      nextTick(() => {
        const numberInput = document.querySelector(`#location-${index}-number`);
        if (numberInput) numberInput.focus();
      });
    }
  } catch (error) {
    console.error('Erro ao buscar CEP:', error);
  } finally {
    searchingCEPIndex.value = -1;
  }
};

// Watch para emitir validação
watch(areAllLocationsValid, (isValid) => {
  emit('validation', isValid);
}, { immediate: true });

// Inicialização
onMounted(() => {
  
  // No modo de edição, marcar todos os campos como tocados
  if (isEditMode.value && props.items.length > 0) {
    
    props.items.forEach((_, index) => {
      ['street', 'number', 'zipCode', 'neighborhood', 'city', 'state'].forEach(field => {
        touchedItems.value[`${index}-${field}`] = true;
      });
    });
    
    
    // Validar todos os itens
    const updatedItems = props.items.map((item, index) => {
      const errors = validateLocation(item, index);
      return { ...item, ...errors };
    });
    
    emit('update', updatedItems);
  }
});

// Cleanup timers before component unmount
onBeforeUnmount(() => {
  if (updateTimer) {
    clearTimeout(updateTimer);
  }
});
</script>

<template>
  <div class="space-y-4">
    <TransitionGroup name="list-fade" tag="div">
      <div 
        v-for="(location, index) in items" 
        :key="`location-${index}`"
        class="relative bg-n-slate-2 dark:bg-n-slate-12/5 p-4 rounded-lg border border-n-slate-5 dark:border-n-slate-10/20 mt-7 location-item dynamic-field-container"
      >
        <!-- Cabeçalho -->
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ items.length > 1 ? `Localização ${items.length - index}` : 'Localização' }}
          </h4>
          <Button
            icon="i-lucide-trash-2"
            color="ruby"
            variant="faded"
            size="xs"
            class="remove-button-rotate"
            @click="removeLocation(index)"
            v-tooltip.top="'Remover localização'"
          />
        </div>
        
        <!-- Grid de campos -->
        <div class="space-y-3">
          <!-- CEP e Botão de Busca -->
          <div class="flex gap-2">
            <div class="flex-1">
              <label class="text-xs text-n-slate-11 mb-1 block">CEP *</label>
              <div class="flex gap-2">
                <Input
                  :id="`location-${index}-zipCode`"
                  :model-value="location.zipCode"
                  placeholder="00000-000"
                  :error="location.zipCodeError"
                  :maxlength="9"
                  @input="(e) => updateLocationField(index, 'zipCode', formatZipCode(e.target.value))"
                  @blur="() => markFieldAsTouched(index, 'zipCode')"
                  @keyup.enter="() => searchCEP(index)"
                  class="flex-1"
                />
                <Button
                  label="Buscar"
                  size="sm"
                  variant="outline"
                  :is-loading="searchingCEPIndex === index"
                  :disabled="!location.zipCode || location.zipCode.replace(/\D/g, '').length !== 8"
                  @click="() => searchCEP(index)"
                  v-tooltip.top="'Buscar endereço pelo CEP'"
                />
              </div>
            </div>
          </div>
          
          <!-- Rua -->
          <div>
            <label class="text-xs text-n-slate-11 mb-1 block">Rua *</label>
            <Input
              :id="`location-${index}-street`"
              :model-value="location.street"
              placeholder="Nome da rua"
              :error="location.streetError"
              @input="(e) => updateLocationField(index, 'street', e.target.value)"
              @blur="() => markFieldAsTouched(index, 'street')"
            />
          </div>
          
          <!-- Número e Complemento -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Número *</label>
              <Input
                :id="`location-${index}-number`"
                :model-value="location.number"
                placeholder="123"
                :error="location.numberError"
                @input="(e) => updateLocationField(index, 'number', e.target.value.replace(/\D/g, ''))"
                @blur="() => markFieldAsTouched(index, 'number')"
              />
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Complemento</label>
              <Input
                :id="`location-${index}-complement`"
                :model-value="location.complement"
                placeholder="Apto, Sala, etc"
                @input="(e) => updateLocationField(index, 'complement', e.target.value)"
              />
            </div>
          </div>
          
          <!-- Bairro -->
          <div>
            <label class="text-xs text-n-slate-11 mb-1 block">Bairro *</label>
            <Input
              :id="`location-${index}-neighborhood`"
              :model-value="location.neighborhood"
              placeholder="Nome do bairro"
              :error="location.neighborhoodError"
              @input="(e) => updateLocationField(index, 'neighborhood', e.target.value)"
              @blur="() => markFieldAsTouched(index, 'neighborhood')"
            />
          </div>
          
          <!-- Cidade e Estado -->
          <div class="grid grid-cols-3 gap-3">
            <div class="col-span-2">
              <label class="text-xs text-n-slate-11 mb-1 block">Cidade *</label>
              <Input
                :id="`location-${index}-city`"
                :model-value="location.city"
                placeholder="Nome da cidade"
                :error="location.cityError"
                @input="(e) => updateLocationField(index, 'city', e.target.value)"
                @blur="() => markFieldAsTouched(index, 'city')"
              />
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Estado *</label>
              <Input
                :id="`location-${index}-state`"
                :model-value="location.state"
                placeholder="UF"
                :error="location.stateError"
                :maxlength="2"
                @input="(e) => updateLocationField(index, 'state', e.target.value.toUpperCase())"
                @blur="() => markFieldAsTouched(index, 'state')"
              />
            </div>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style lang="scss" scoped>
// Importar animações compartilhadas
@import '../shared/DynamicFieldAnimations.scss';

// Estilos específicos para localização
.location-item {
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  }
  
  // Efeito de onda ao adicionar
  &.highlight-new-item {
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0;
      height: 0;
      border-radius: 50%;
      background: var(--primary-100);
      transform: translate(-50%, -50%);
      animation: ripple 0.6s ease-out;
    }
  }
}

@keyframes ripple {
  to {
    width: 100%;
    height: 100%;
    opacity: 0;
  }
}

// Usa a animação do arquivo compartilhado
// .remove-button-rotate está definido em DynamicFieldAnimations.scss

// Animação do botão buscar CEP
.cep-search-button {
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:not(:disabled):hover {
    transform: translateX(2px);
  }
}

// Feedback visual ao preencher automaticamente
.auto-filled {
  animation: autoFillHighlight 0.8s ease-out;
}

@keyframes autoFillHighlight {
  0% {
    background-color: var(--primary-50);
  }
  100% {
    background-color: transparent;
  }
}
</style>